import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

interface ErrorStateProps {
  title?: string;
  message?: string;
  error?: any;
  onRetry?: () => void;
  height?: string | number;
  width?: string | number;
  showBackground?: boolean;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message = 'We encountered an error while loading the data.',
  error,
  onRetry,
  height = '200px',
  width = '100%',
  showBackground = true,
}) => {
  const getErrorMessage = () => {
    if (error?.response?.status === 404) {
      return 'No data found for the selected criteria.';
    }
    if (error?.response?.status === 401) {
      return 'Authentication required. Please log in again.';
    }
    if (error?.response?.status === 403) {
      return 'You do not have permission to access this data.';
    }
    if (error?.response?.status >= 500) {
      return 'Server error. Please try again later.';
    }
    if (error?.message) {
      return error.message;
    }
    return message;
  };

  return (
    <Box
      sx={{
        height,
        width,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        background: showBackground ? '#fef2f2' : 'transparent',
        borderRadius: showBackground ? '8px' : '0',
        border: showBackground ? '1px solid #fecaca' : 'none',
        padding: 3,
      }}
    >
      <ErrorOutlineIcon 
        sx={{ 
          fontSize: 48, 
          color: '#ef4444',
          opacity: 0.8,
        }} 
      />
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h6"
          sx={{
            color: '#dc2626',
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#7f1d1d',
            fontSize: '14px',
            lineHeight: 1.5,
            maxWidth: '400px',
          }}
        >
          {getErrorMessage()}
        </Typography>
      </Box>
      {onRetry && (
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          sx={{
            color: '#dc2626',
            borderColor: '#dc2626',
            '&:hover': {
              backgroundColor: '#fef2f2',
              borderColor: '#b91c1c',
            },
          }}
        >
          Try Again
        </Button>
      )}
    </Box>
  );
}; 